import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, RefreshCw, Hash } from "lucide-react";
import { toast } from "sonner";

const COUNTRIES = [
  { v: "united-states", l: "US" },
  { v: "united-kingdom", l: "UK" },
  { v: "india", l: "IN" },
  { v: "canada", l: "CA" },
  { v: "australia", l: "AU" },
  { v: "japan", l: "JP" },
  { v: "brazil", l: "BR" },
  { v: "worldwide", l: "WW" },
];

export default function TrendsPanel({ onPick, compact = false, defaultCountry = "united-states" }) {
  const [country, setCountry] = useState(defaultCountry);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = async (c) => {
    setLoading(true);
    try {
      const res = await api.get(`/trends?country=${c}`);
      setTrends(res.data.trends || []);
      setFetchedAt(res.data.fetched_at);
    } catch (e) {
      toast.error("Trends fetch failed.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(country); /* eslint-disable-next-line */ }, [country]);

  const max = compact ? 8 : 25;

  return (
    <div className="brutal-border bg-[#141414]" data-testid="trends-panel">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} className="text-signal-yellow" />
          <span className="label-eyebrow">// live X/twitter trends</span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {COUNTRIES.map((c) => (
            <button
              key={c.v}
              data-testid={`trend-country-${c.v}`}
              onClick={() => setCountry(c.v)}
              className={`font-mono text-[10px] px-2 py-1 border transition-colors ${
                country === c.v
                  ? "bg-signal-yellow text-black border-signal-yellow"
                  : "border-white/10 text-zinc-500 hover:text-white hover:border-white/30"
              }`}
            >
              {c.l}
            </button>
          ))}
          <button
            data-testid="trend-refresh"
            onClick={() => load(country)}
            className="ml-1 border border-white/10 text-zinc-500 hover:text-signal-yellow hover:border-signal-yellow px-2 py-1 transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="p-3 max-h-96 overflow-auto">
        {loading && trends.length === 0 ? (
          <div className="font-mono text-xs text-zinc-500 animate-pulse p-3">// pulling trending signals...</div>
        ) : trends.length === 0 ? (
          <div className="font-mono text-xs text-zinc-500 p-3">// no trends available.</div>
        ) : (
          <ul className="space-y-1">
            {trends.slice(0, max).map((t, i) => (
              <li key={`${t.name}-${i}`}>
                <button
                  data-testid={`trend-item-${i}`}
                  onClick={() => onPick?.(t.name)}
                  className="w-full text-left px-3 py-2 hover:bg-white/5 group flex items-center justify-between gap-3 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[10px] text-zinc-600 w-5">{String(i + 1).padStart(2, "0")}</span>
                    {t.is_hashtag ? <Hash size={11} className="text-signal-yellow shrink-0" /> : <span className="w-3" />}
                    <span className="font-mono text-xs text-zinc-200 truncate group-hover:text-signal-yellow">{t.name}</span>
                  </div>
                  {t.meta && <span className="font-mono text-[10px] text-zinc-600 shrink-0">{t.meta}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {fetchedAt && (
        <div className="px-4 py-2 border-t border-white/10 font-mono text-[9px] text-zinc-600 tracking-[0.2em] uppercase">
          // synced {new Date(fetchedAt * 1000).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}
